<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { usePermissions } from '@/composables/usePermissions';
import type { GetPlayersByIdResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type PlayerInfo = Partial<GetPlayersByIdResponse['payload']>;
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const route = useRoute();
const { t } = useI18n();
const { hasPermission } = usePermissions();
const { checkRequest, checkRoute } = useDetectPermissions();
//#endregion

//#region state
const info = ref<PlayerInfo>({});
const isDataLoading = ref(true);
//#endregion

//#region computed
const isSuperuser = computed(() => store.getters['auth/adminAcl'].is_superuser);
const playerId = computed(() => route.params.id as string);

const backPage = computed(() => {
  const raw = route.query.param_playerlist;
  if (typeof raw !== 'string' || !raw) {
    return '/players/list';
  }
  return raw.replace(/back=/gi, '');
});

const breadcrumbs = computed(() => {
  return [
    {
      title: t('players.menu.playersList'),
      link: backPage.value,
    },
    {
      title: info.value.site_player_id ?? '',
    },
  ];
});

const playerNavigation = computed(() => {
  const items = [
    {
      name: t('players.nav.info'),
      key: 'info',
      routeName: 'players.item.info',
    },
  ];

  if (canViewLastOperations()) {
    items.push({
      name: t('players.nav.last'),
      key: 'last',
      routeName: 'players.item.last',
    });
  }

  if (hasPermission('crm_activities_view')) {
    items.push({
      name: t('players.nav.crm'),
      key: 'crm',
      routeName: 'players.item.crm',
    });
  }

  return items;
});

const childBindings = computed(() => {
  if (route.name !== 'players.item.info') {
    return {};
  }
  return {
    info: info.value,
    'onUpdate:info': onUpdateInfo,
  };
});
//#endregion

//#region methods
function initData() {
  if (checkRequest('players_view')) {
    fetchData();
  }
  checkRoute('players_view');
}

function fetchData() {
  isDataLoading.value = true;
  api
    .getPlayerInfo(playerId.value)
    .then((res) => {
      info.value = res.data.payload;
    })
    .finally(() => {
      isDataLoading.value = false;
    });
}

function canViewLastOperations() {
  return store.getters['auth/currentAcl'].last_bets_and_deposits_view === 'allow'
    || (isSuperuser.value && !localStorage.getItem('active-team'));
}

function onUpdateInfo(value: PlayerInfo) {
  info.value = value;
}
//#endregion

//#region watch
watch(() => route.params.id, () => {
  info.value = {};
  fetchData();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div
    v-loading="isDataLoading"
    class="player-info"
  >
    <Breadcrumbs :items="breadcrumbs" />
    <UiToolbar>
      <template v-slot:title>
        {{ info.site_player_id }}
      </template>
    </UiToolbar>
    <CardNavigation
      :list="playerNavigation"
      :params="{ id: playerId }"
      :query="route.query"
      class="t:mb-16"
    />
    <RouterView v-slot="{ Component }">
      <component
        :is="Component"
        :key="playerId"
        v-bind="childBindings"
      />
    </RouterView>
  </div>
</template>
